import { type ReactNode } from "react";
import { Download, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export interface ExportFormat<F extends string = string> {
  /** Machine value passed to `onExport` (e.g. "csv", "txt-phones"). */
  value: F;
  /** Visible label (e.g. "CSV", "Только телефоны"). */
  label: ReactNode;
  icon?: ReactNode;
}

export interface ExportMenuProps<F extends string = string> {
  formats: ExportFormat<F>[];
  onExport: (format: F) => void;
  /** Trigger button label. Defaults to "Экспорт". */
  label?: ReactNode;
  disabled?: boolean;
}

/**
 * ExportMenu — DropdownMenu specialization (DESIGN-SYSTEM §5).
 * Trigger "Экспорт ⌄" with a Download icon; items are the supported formats.
 * Replaces the multi-button export row on the Users page.
 */
export function ExportMenu<F extends string = string>({
  formats,
  onExport,
  label = "Экспорт",
  disabled = false,
}: ExportMenuProps<F>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          iconLeft={<Download size={16} strokeWidth={2} aria-hidden="true" />}
          iconRight={<ChevronDown size={16} strokeWidth={2} aria-hidden="true" />}
        >
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {formats.map((f) => (
          <DropdownMenuItem key={f.value} icon={f.icon} onSelect={() => onExport(f.value)}>
            {f.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
ExportMenu.displayName = "ExportMenu";
